<script setup lang="ts">
import type { SizesType } from '@ui/styles'
import type { ListItemType } from './ListTypes'
import { fromArray, toArray } from '@ui/utils/array'
import ListItem from './ListItem.vue'

const props = withDefaults(
  defineProps<{
    multiple?: boolean
    items?: ListItemType[]
    flag?: boolean
    dark?: boolean
    disabled?: boolean
    empty?: string
    size?: SizesType
    variant?: 'label' | 'title' | 'button' | 'divider'
  }>(),
  {
    multiple: false,
    items: () => [],
    flag: false,
    dark: false,
    disabled: false,
    empty: '',
    size: 'leia',
    variant: 'label',
  },
)

const emit = defineEmits(['select'])

defineSlots<{
  default?: () => void
  left?: (props: { value: ListItemType['value']; checked: boolean; item?: ListItemType }) => void
  text?: (props: {
    value: ListItemType['value']
    label: string
    hint: string
    checked: boolean
    item?: ListItemType
  }) => void
  right?: (props: { value: ListItemType['value']; checked: boolean; item?: ListItemType }) => void
}>()

const model = defineModel<ListItemType['value'][] | ListItemType['value']>()

function getItemKey(item: ListItemType, index: number): PropertyKey {
  return typeof item.value === 'string' || typeof item.value === 'number' ? item.value : `${item.label}-${index}`
}

function check(item: ListItemType) {
  emit('select', item)
  if (props.multiple) {
    const v = toArray(model.value)
    if (!v.includes(item.value)) {
      v.push(item.value)
      model.value = [...v]
    } else {
      model.value = v.filter(e => e !== item.value)
    }
  } else {
    const v = fromArray(model.value)
    if (v !== item.value) {
      model.value = item.value
    }
  }
}
</script>

<template lang="pug">
ul.flex.col-8.p-0.m-0
  slot
    ListItem(
      v-for="(item, index) of items"
      :key="getItemKey(item, index)"
      :label="item.label"
      :hint="item.hint"
      :value="item.value"
      :multiple="multiple"
      :modelValue="model"
      :flag="flag"
      :dark="dark"
      :disabled="disabled"
      :size="size"
      :variant="variant"
      @click="!disabled && check(item)"
    )
      template(#left="slotProps")
        slot(name="left" v-bind="slotProps" :item="item")
      template(#default="slotProps")
        slot(name="text" v-bind="slotProps" :item="item")
      template(#right="slotProps")
        slot(name="right" v-bind="slotProps" :item="item")
    ListItem(v-if="!items.length" :disabled="true" :size="size" :variant="variant") {{ $t(empty) }}
</template>
